import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, GraduationCap, Check } from 'lucide-react';
import { motion } from 'framer-motion';

const PROGRAM_IMAGES = {
  k5: 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/yxdn1mzg_iStock-1373258176.jpg',
  middle: 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/sbwj184j_iStock-2148802894.jpg',
  high: 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/zkqvcfhw_iStock-973279516.jpg',
};

export default function ReturningFamiliesPage() {
  const { language } = useLanguage();

  const levels = language === 'fr' ? [
    {
      title: 'Élémentaire (GS-CM2)',
      grades: 'K-5',
      image: PROGRAM_IMAGES.k5,
      color: '#66CA9D',
      fluent: {
        profile: 'Enfant ayant un niveau avancé en anglais (bilingue, retour d\'expatriation, famille anglophone)',
        content: [
          'Lecture et narration de niveau avancé',
          'Production d\'écrits',
          'Vocabulaire et expression orale enrichis',
          'Histoire et culture américaine dès le CM1',
        ],
      },
      bridge: {
        profile: 'Enfant en cours d\'apprentissage de l\'anglais, scolarisé en France',
        content: [
          'Immersion progressive en anglais',
          'Lecture et écriture adaptées au niveau',
          'Enrichissement du vocabulaire',
          'Découverte de la culture américaine dès le CM1',
        ],
      },
    },
    {
      title: 'Collège (6e-4e)',
      grades: '6-8',
      image: PROGRAM_IMAGES.middle,
      color: '#F8CD46',
      fluent: {
        profile: 'Élève avec un niveau avancé, capable de suivre un cursus académique en anglais',
        content: [
          'Analyse littéraire avancée',
          'Rédaction analytique et argumentative',
          'Social Studies : US History, World History',
          'Pensée critique et débat',
        ],
      },
      bridge: {
        profile: 'Élève qui progresse en anglais et veut atteindre un niveau bilingue',
        content: [
          'Compréhension et analyse de textes adaptés',
          'Rédaction guidée et progressive',
          'Social Studies intégrés selon le niveau',
          'Développement de la pensée critique',
        ],
      },
    },
    {
      title: 'Lycée (3e-Terminale)',
      grades: '9-12',
      image: PROGRAM_IMAGES.high,
      color: '#2D78F6',
      fluent: {
        profile: 'Lycéen bilingue visant le diplôme et les universités internationales',
        content: [
          'Littérature et Composition avancées',
          'US History (2 crédits — niveau avancé)',
          'Préparation SAT/TOEFL',
          'Orientation universitaire',
        ],
      },
      bridge: {
        profile: 'Lycéen qui construit sa maîtrise de l\'anglais vers l\'excellence bilingue',
        content: [
          'Littérature et Composition (niveau adapté)',
          'US History (2 crédits — niveau adapté)',
          'Renforcement linguistique intensif',
          'Orientation universitaire',
        ],
      },
    },
  ] : [
    {
      title: 'Elementary (K-5)',
      grades: 'K-5',
      image: PROGRAM_IMAGES.k5,
      color: '#66CA9D',
      fluent: {
        profile: 'Child with advanced English (bilingual, returning expat, anglophone family)',
        content: [
          'Advanced-level reading and storytelling',
          'Written production',
          'Enriched vocabulary and oral expression',
          'American history and culture from 4th grade',
        ],
      },
      bridge: {
        profile: 'Child learning English, enrolled in French school',
        content: [
          'Progressive English immersion',
          'Level-adapted reading and writing',
          'Vocabulary enrichment',
          'American culture discovery from 4th grade',
        ],
      },
    },
    {
      title: 'Middle School (6-8)',
      grades: '6-8',
      image: PROGRAM_IMAGES.middle,
      color: '#F8CD46',
      fluent: {
        profile: 'Student with advanced level, able to follow academic coursework in English',
        content: [
          'Advanced literary analysis',
          'Analytical and argumentative writing',
          'Social Studies: US History, World History',
          'Critical thinking and debate',
        ],
      },
      bridge: {
        profile: 'Student progressing in English, aiming for bilingual level',
        content: [
          'Adapted text comprehension and analysis',
          'Guided and progressive writing',
          'Social Studies integrated based on level',
          'Critical thinking development',
        ],
      },
    },
    {
      title: 'High School (9-12)',
      grades: '9-12',
      image: PROGRAM_IMAGES.high,
      color: '#2D78F6',
      fluent: {
        profile: 'Bilingual student targeting the diploma and international universities',
        content: [
          'Advanced Literature and Composition',
          'US History (2 credits — advanced level)',
          'SAT/TOEFL Preparation',
          'College counseling',
        ],
      },
      bridge: {
        profile: 'Student building English mastery toward bilingual excellence',
        content: [
          'Literature and Composition (adapted level)',
          'US History (2 credits — adapted level)',
          'Intensive language reinforcement',
          'College counseling',
        ],
      },
    },
  ];

  return (
    <div data-testid="returning-families-page" className="min-h-screen bg-white">
      <SEO path="/returning-families" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Quel parcours pour votre enfant ?' : 'Which track for your child?'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/70 max-w-3xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'Chaque élève a un profil différent. OFA France propose deux parcours pour que chacun progresse vers l\'excellence bilingue, quel que soit son point de départ.'
              : 'Every student has a different profile. OFA France offers two tracks so each student can progress toward bilingual excellence, regardless of their starting point.'}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Intro SEO */}
      <section className="py-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#14274B] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Chaque enfant a un parcours différent avec l\'anglais. C\'est pourquoi OFA France propose deux parcours distincts, conçus pour répondre aux besoins spécifiques de chaque famille. Que votre enfant soit déjà bilingue, de retour d\'expatriation, issu d\'une famille anglophone en France, ou qu\'il souhaite simplement acquérir un niveau avancé en anglais, nous avons le programme qu\'il lui faut.'
              : 'Every child has a different journey with English. That\'s why OFA France offers two distinct tracks, designed to meet the specific needs of each family. Whether your child is already bilingual, returning from expatriation, from an English-speaking family in France, or simply wishes to achieve an advanced level of English, we have the right program for them.'}
          </p>
          <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'L\'OFA Fluent Track est conçu pour les élèves ayant déjà un niveau avancé en anglais. L\'OFA Bridge Track est destiné aux élèves qui souhaitent progresser vers le bilinguisme avec un accompagnement adapté. Les deux parcours mènent au même objectif : le U.S. High School Diploma accrédité COGNIA. Comparez les deux parcours ci-dessous pour trouver celui qui correspond le mieux à votre enfant.'
              : 'The OFA Fluent Track is designed for students who already have an advanced level of English. The OFA Bridge Track is for students who wish to progress toward bilingualism with adapted support. Both tracks lead to the same goal: the COGNIA-accredited U.S. High School Diploma. Compare both tracks below to find the one that best suits your child.'}
          </p>
        </div>
      </section>

      {/* Track Headers */}
      <section className="bg-white border-b border-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {/* Fluent Track Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="p-8 bg-[#2D78F6] text-white text-center"
            >
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Rester Bilingue' : 'Stay Bilingual'}
              </h2>
              <p className="text-sm font-medium text-white/80 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                OFA Fluent Track
              </p>
              <p className="text-white/70 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Pour les élèves ayant déjà un niveau avancé en anglais : enfants bilingues, retour d\'expatriation, familles anglophones en France.'
                  : 'For students with an advanced English level: bilingual children, returning expats, anglophone families in France.'}
              </p>
            </motion.div>
            {/* Bridge Track Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="p-8 bg-[#66CA9D] text-white text-center"
            >
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Devenir Bilingue' : 'Become Bilingual'}
              </h2>
              <p className="text-sm font-medium text-white/80 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                OFA Bridge Track
              </p>
              <p className="text-white/70 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Pour les familles qui souhaitent offrir une réelle maîtrise de l\'anglais à leur enfant, avec un accompagnement progressif et adapté.'
                  : 'For families who want to offer true English mastery to their child, with progressive and adapted support.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Level Comparisons */}
      {levels.map((level, index) => (
        <section key={index} className={`py-12 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Level Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-4 mb-8"
            >
              <div
                className="px-4 py-2 text-sm font-medium text-white"
                style={{ backgroundColor: level.color, fontFamily: 'Poppins, sans-serif' }}
              >
                {level.grades}
              </div>
              <h2
                className="text-2xl md:text-3xl font-light text-[#14274B]"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {level.title}
              </h2>
            </motion.div>

            {/* Two Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Fluent */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="border-2 border-[#2D78F6]/20 p-6 text-center"
              >
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-3 h-3 bg-[#2D78F6] rounded-full" />
                  <h3 className="text-lg font-semibold text-[#2D78F6]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                    {language === 'fr' ? 'Rester Bilingue' : 'Stay Bilingual'}
                  </h3>
                </div>
                <p className="text-sm text-[#475569] italic mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {level.fluent.profile}
                </p>
                <ul className="space-y-2 inline-block text-left">
                  {level.fluent.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#2D78F6] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#14274B]" style={{ fontFamily: 'Poppins, sans-serif' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Bridge */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="border-2 border-[#66CA9D]/20 p-6 text-center"
              >
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-3 h-3 bg-[#66CA9D] rounded-full" />
                  <h3 className="text-lg font-semibold text-[#66CA9D]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                    {language === 'fr' ? 'Devenir Bilingue' : 'Become Bilingual'}
                  </h3>
                </div>
                <p className="text-sm text-[#475569] italic mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {level.bridge.profile}
                </p>
                <ul className="space-y-2 inline-block text-left">
                  {level.bridge.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#66CA9D] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#14274B]" style={{ fontFamily: 'Poppins, sans-serif' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Common Diploma */}
      <section className="py-16 bg-[#14274B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <GraduationCap className="w-12 h-12 text-[#F8CD46] mx-auto mb-6" />
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Un objectif commun' : 'A common goal'}
          </h2>
          <p
            className="text-xl text-[#F8CD46] font-semibold mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            Bilingual High School Diploma
          </p>
          <p className="text-white/70 max-w-2xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Les deux parcours mènent au même diplôme. La différence se situe dans le niveau d\'exigence linguistique et la vitesse de progression, pas dans la destination.'
              : 'Both tracks lead to the same diploma. The difference lies in the level of linguistic demand and the pace of progression, not in the destination.'}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Vous ne savez pas quel parcours choisir ?' : 'Not sure which track to choose?'}
          </h2>
          <p className="text-white/80 mb-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Notre équipe évalue le niveau de chaque élève à l\'admission et recommande le parcours le plus adapté.'
              : 'Our team assesses each student\'s level at admission and recommends the most suitable track.'}
          </p>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="tracks-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {language === 'fr' ? 'S\'inscrire' : 'Register'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
